// (2026-06-09) MAESTRO de Compras — FORMULARIO como PAGINA (estilo FormProformas).
//
// Reemplaza el modal de "Nueva compra" de ComprasPage.js por una pagina con
// rutas /compras/facturas/create y /compras/facturas/edit?id=<cifrado>.
// Conserva EXACTAMENTE la logica de negocio existente del modulo de Compras:
//   - createCompra({ ...form, items })  /  updateCompra(id, { ...form, items })
//   - mismos campos del payload (proveedor_id, numero_factura_proveedor,
//     numero_control_proveedor, tipo_documento, fecha_emision, moneda_id,
//     aplica_retencion_iva, aplica_retencion_islr, observaciones, items[])
//   - misma validacion (proveedor + nº factura + nº control + >=1 item con precio)
//   - mismo calculo de total estimado (cantidad * precio * (1 + alicuota/100))
//
// El id a editar se lee igual que el maestro (FormClients/FormProformas):
// query param ?id= cifrado con encryptText, descifrado con decryptText.
// Tras guardar, navega de vuelta a la lista (/compras/facturas).
//
// Reusa los primitivos de ./ui (NADA de @mui) y la guia _DESIGN_GUIDE.md.

import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../../context/AuthContext";
import AdminLayout from "../../layouts/AdminLayout";
import { decryptText } from "../../controllers/api";
import {
  getCompra, createCompra, updateCompra,
  getProveedores, getIvaCategorias,
} from "../../controllers/api_compras";
import { MONEDAS, monedaCodigo, fmt, handleApiError, hoyISO, upper } from "./comprasShared";
import {
  Card, Field, TextInput, SelectInput, Toggle, SectionTitle, IconBtn,
} from "./ui";
import {
  preventEnterSubmit, FORM_FOOTER_CONTAINER, FORM_BTN_CANCEL, FORM_BTN_SUBMIT,
} from "../../utils/formHelpers";

const lineaVacia = () => ({ descripcion: "", cantidad: 1, precio_unitario: "", aplica_iva: true, iva_categoria_id: "" });

const EMPTY = {
  proveedor_id: "", numero_factura_proveedor: "", numero_control_proveedor: "",
  tipo_documento: "FC", fecha_emision: hoyISO(), moneda_id: 3,
  aplica_retencion_iva: false, aplica_retencion_islr: false, observaciones: "",
};

export default function CompraForm() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const rawId = queryParams.get("id"); // cifrado (encryptText) cuando se edita
  const compraId = rawId ? decryptText(rawId) : null;
  const esEdicion = !!compraId;

  const [provs, setProvs] = useState([]);
  const [ivas, setIvas] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [items, setItems] = useState([lineaVacia()]);
  const [loading, setLoading] = useState(true);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [errors, setErrors] = useState({}); // Estado para errores de validacion (estilo maestro)

  // Carga de catalogos + (si edita) la compra a editar.
  useEffect(() => {
    let activo = true;
    const cargar = async () => {
      try {
        getProveedores({ activo: 1 }).then((d) => activo && setProvs(Array.isArray(d) ? d : (d?.data || []))).catch(() => {});
        getIvaCategorias().then((d) => activo && setIvas(Array.isArray(d) ? d : (d?.data || []))).catch(() => {});
        if (esEdicion) {
          const c = await getCompra(compraId);
          if (!activo) return;
          // Solo el estatus 'borrador' es editable (regla del maestro/lista).
          if (c && c.estatus && c.estatus !== "borrador") {
            toast.warn("Solo las compras en estatus borrador pueden editarse.");
            setTimeout(() => navigate("/compras/facturas"), 1500);
            return;
          }
          setForm({
            proveedor_id: c.proveedor_id ?? "",
            numero_factura_proveedor: c.numero_factura_proveedor ?? "",
            numero_control_proveedor: c.numero_control_proveedor ?? "",
            tipo_documento: c.tipo_documento ?? "FC",
            fecha_emision: (c.fecha_emision || "").slice(0, 10) || hoyISO(),
            moneda_id: c.moneda_id ?? 3,
            aplica_retencion_iva: !!c.aplica_retencion_iva,
            aplica_retencion_islr: !!c.aplica_retencion_islr,
            observaciones: c.observaciones ?? "",
          });
          const lns = Array.isArray(c.items) ? c.items : [];
          setItems(
            lns.length
              ? lns.map((it) => ({
                  descripcion: it.descripcion ?? "",
                  cantidad: it.cantidad ?? 1,
                  precio_unitario: it.precio_unitario ?? "",
                  aplica_iva: it.aplica_iva !== false,
                  iva_categoria_id: it.iva_categoria_id ?? "",
                }))
              : [lineaVacia()]
          );
        }
      } catch (e) {
        handleApiError(e, "No se pudo cargar la compra.");
      } finally {
        if (activo) setLoading(false);
      }
    };
    cargar();
    return () => { activo = false; };
  }, [compraId]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addRow = () => setItems((a) => [...a, lineaVacia()]);
  const delRow = (i) => setItems((a) => a.filter((_, idx) => idx !== i));

  // Total estimado — calculo IDENTICO al de ComprasPage.js.
  const total = items.reduce((s, it) => {
    const base = Number(it.cantidad || 0) * Number(it.precio_unitario || 0);
    const alic = it.aplica_iva
      ? (Number((ivas.find((x) => x.id === Number(it.iva_categoria_id)) || {}).tasa_porcentaje) || 0)
      : 0;
    return s + base * (1 + alic / 100);
  }, 0);

  const redirectToList = () => navigate("/compras/facturas");

  // Validacion espejo del backend (estilo maestro FormProformas):
  // arma newErrors + errorToast, hace setErrors, muestra un unico toast.error
  // con todos los mensajes y devuelve booleano (true = sin errores).
  const validate = () => {
    const newErrors = {};
    var errorToast = [];

    if (!form.proveedor_id) {
      newErrors.proveedor_id = "Proveedor es obligatorio";
      errorToast.push("- Debe seleccionar el proveedor.");
    }
    if (!form.numero_factura_proveedor) {
      newErrors.numero_factura_proveedor = "Nº factura del proveedor es obligatorio";
      errorToast.push("- Debe indicar el nº de factura del proveedor.");
    }
    if (!form.numero_control_proveedor) {
      newErrors.numero_control_proveedor = "Nº control del proveedor es obligatorio";
      errorToast.push("- Debe indicar el nº de control del proveedor.");
    }
    if (!form.fecha_emision) {
      newErrors.fecha_emision = "Fecha de emisión es obligatoria";
      errorToast.push("- Debe indicar la fecha de emisión.");
    }

    // Al menos un item valido: descripcion + cantidad > 0 + precio_unitario > 0.
    const itemsValidos = items.filter(
      (it) => it.descripcion && Number(it.cantidad) > 0 && Number(it.precio_unitario) > 0
    );
    if (!itemsValidos.length) {
      newErrors.items = "Debe agregar al menos un ítem válido";
      errorToast.push("- Debe agregar al menos un ítem válido.");
    }

    // Si un item aplica IVA pero no eligio alicuota/categoria, error en esa linea.
    for (var i = 0; i < items.length; i++) {
      const it = items[i];
      const lineaCargada = it.descripcion || Number(it.cantidad) > 0 || Number(it.precio_unitario) > 0;
      if (lineaCargada && it.aplica_iva && !it.iva_categoria_id) {
        errorToast.push(`- El ítem ${i + 1} aplica IVA pero no tiene alícuota seleccionada.`);
        newErrors.items = newErrors.items || "Alícuota requerida en ítems con IVA";
      }
    }

    setErrors(newErrors);
    if (errorToast.length > 0) {
      toast.error(<div>
        {errorToast.map(item => (
            <span className="text-start">{item}<br/></span>
          ))}
      </div>);
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setButtonDisabled(true);

    if (!validate()) {
      setButtonDisabled(false);
      return;
    }

    // Normaliza items validos para el payload (misma forma que antes).
    const validos = items
      .filter((it) => it.descripcion && Number(it.cantidad) > 0 && Number(it.precio_unitario) > 0)
      .map((it) => ({ ...it, iva_categoria_id: it.aplica_iva ? (it.iva_categoria_id || null) : null }));

    try {
      if (esEdicion) {
        await updateCompra(compraId, { ...form, items: validos });
        toast.success("Compra actualizada.", { onClose: redirectToList, autoClose: 1500 });
      } else {
        await createCompra({ ...form, items: validos });
        toast.success("Compra creada (borrador).", { onClose: redirectToList, autoClose: 1500 });
      }
      setTimeout(redirectToList, 2000);
    } catch (err) {
      handleApiError(err, "No se pudo guardar la compra.");
      setButtonDisabled(false);
    }
  };

  return (
    <AdminLayout nombreUsuario={user?.nombre || "Usuario"}>
      <ToastContainer position="bottom-right" autoClose={3000} />
      <div className="mt-8 px-4 md:px-8">
        <Card>
          {/* Header de la pagina-formulario */}
          <div className="rounded-t bg-white mb-0 px-6 py-6 flex justify-between items-center border-b">
            <h6 className="text-blueGray-700 text-xl font-bold flex items-center gap-2">
              <i className="fas fa-file-invoice-dollar text-sirius-600" aria-hidden="true" />
              {esEdicion ? "Actualizar compra" : "Nueva compra"} (factura de proveedor)
            </h6>
          </div>

          <div className="flex-auto px-4 lg:px-10 py-10 pt-6">
            {loading ? (
              <div className="text-center text-blueGray-400 italic py-10">Cargando...</div>
            ) : (
              <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
                {/* ----- Cabecera de la factura ----- */}
                <SectionTitle icon="fa-file-lines">Datos de la factura</SectionTitle>
                <div className="flex flex-wrap -mx-4">
                  <Field label="Proveedor *" error={errors.proveedor_id}>
                    <SelectInput value={form.proveedor_id} onChange={set("proveedor_id")} options={provs} placeholder="Seleccione..." />
                  </Field>
                  <Field label="Nº Factura proveedor *" error={errors.numero_factura_proveedor}>
                    <TextInput value={form.numero_factura_proveedor} onChange={set("numero_factura_proveedor")} />
                  </Field>
                  <Field label="Nº Control proveedor *" error={errors.numero_control_proveedor}>
                    <TextInput value={form.numero_control_proveedor} onChange={set("numero_control_proveedor")} />
                  </Field>
                  <Field label="Tipo doc.">
                    <SelectInput value={form.tipo_documento} onChange={set("tipo_documento")}>
                      <option value="FC">Factura</option>
                      <option value="NC">Nota de Crédito</option>
                      <option value="ND">Nota de Débito</option>
                      <option value="IMP">Importación</option>
                    </SelectInput>
                  </Field>
                  <Field label="Fecha emisión *" error={errors.fecha_emision}>
                    <TextInput type="date" value={form.fecha_emision} onChange={set("fecha_emision")} />
                  </Field>
                  <Field label="Moneda">
                    <SelectInput value={form.moneda_id} onChange={set("moneda_id")}>
                      {MONEDAS.map((m) => <option key={m.id} value={m.id}>{m.codigo}</option>)}
                    </SelectInput>
                  </Field>
                </div>

                {/* Retenciones */}
                <div className="px-4 mt-2 flex flex-wrap items-center gap-6">
                  <Toggle checked={form.aplica_retencion_iva} onChange={set("aplica_retencion_iva")} label="Retener IVA al proveedor" />
                  <Toggle checked={form.aplica_retencion_islr} onChange={set("aplica_retencion_islr")} label="Retener ISLR al proveedor" />
                </div>

                {/* Observaciones */}
                <div className="flex flex-wrap -mx-4 mt-2">
                  <Field label="Observaciones" className="w-full px-4">
                    <TextInput value={form.observaciones} onChange={set("observaciones")} placeholder="OBSERVACIONES (OPCIONAL)" />
                  </Field>
                </div>

                {/* ----- Editor de items (estructura del form de factura) ----- */}
                <SectionTitle icon="fa-list">Ítems</SectionTitle>

                {/* Encabezado de columnas (visible en lg+) */}
                <div className="hidden lg:flex items-center gap-2 px-4 mb-2 text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-500">
                  <div className="flex-[3]">Descripción</div>
                  <div className="flex-1">Cant.</div>
                  <div className="flex-[1.5]">Precio unit.</div>
                  <div className="flex-1 text-center">IVA</div>
                  <div className="flex-[2]">Alícuota</div>
                  <div className="w-9" />
                </div>

                {items.map((it, i) => (
                  <div key={i} className="flex flex-col lg:flex-row lg:items-center gap-2 px-4 mb-3 lg:mb-2">
                    <div className="flex-[3]">
                      <TextInput placeholder="DESCRIPCIÓN" value={it.descripcion} onChange={(v) => setItem(i, "descripcion", upper(v))} />
                    </div>
                    <div className="flex-1">
                      <TextInput type="number" placeholder="Cant." value={it.cantidad} onChange={(v) => setItem(i, "cantidad", v)} />
                    </div>
                    <div className="flex-[1.5]">
                      <TextInput type="number" placeholder="Precio" value={it.precio_unitario} onChange={(v) => setItem(i, "precio_unitario", v)} />
                    </div>
                    <div className="flex-1 flex justify-start lg:justify-center py-2 lg:py-0">
                      <Toggle checked={it.aplica_iva} onChange={(v) => setItem(i, "aplica_iva", v)} label="IVA" />
                    </div>
                    <div className="flex-[2]">
                      <SelectInput value={it.aplica_iva ? it.iva_categoria_id : ""} onChange={(v) => setItem(i, "iva_categoria_id", v)} disabled={!it.aplica_iva} placeholder="Alícuota">
                        {ivas.map((x) => <option key={x.id} value={x.id}>{x.tasa_porcentaje}%</option>)}
                      </SelectInput>
                    </div>
                    <div className="w-9 flex justify-center shrink-0">
                      <IconBtn icon="fa-trash-can" title="Eliminar ítem" color="text-red-600 hover:text-red-800" onClick={() => delRow(i)} />
                    </div>
                  </div>
                ))}

                <div className="px-4 mt-3 flex flex-wrap items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={addRow}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sirius-50 text-sirius-700 font-display font-semibold hover:bg-sirius-100 transition"
                  >
                    <i className="fas fa-plus" aria-hidden="true" />
                    Agregar ítem
                  </button>
                  <p className="text-base md:text-lg font-display font-bold text-ink-900">
                    Total estimado:{" "}
                    <span className="text-sirius-600">{fmt(total)} {monedaCodigo(form.moneda_id)}</span>
                  </p>
                </div>

                {/* ----- Botones ----- */}
                <div className={FORM_FOOTER_CONTAINER}>
                  <button type="button" className={FORM_BTN_CANCEL} disabled={buttonDisabled} onClick={redirectToList}>
                    Cancelar
                  </button>
                  <button type="submit" className={FORM_BTN_SUBMIT} disabled={buttonDisabled}>
                    {buttonDisabled
                      ? (esEdicion ? "Actualizando..." : "Guardando...")
                      : (esEdicion ? "Actualizar" : "Guardar")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}
